// hooks/playground/scenario/useScenarioModalState.js
import { useState, useEffect, useMemo } from 'react';

export const useScenarioModalState = ({
  scenarios,
  currentScenarioId,
  adjustments,      // Current working adjustments (for reference)
  customItems,      // Current working custom items (for reference)
  currentYear,
  currentMonth,
}) => {
  // Edit mode state
  const [editMode, setEditMode] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Derived state
  const currentScenario = scenarios.find(s => s.scenario_id === currentScenarioId);
  const isActualView = currentScenarioId === null;

  // ✅ Calculate adjustment counts from SCENARIO data (not current working data)
  const adjustmentCount = useMemo(() => {
    if (!currentScenario?.adjustments) return 0;
    
    return Object.keys(currentScenario.adjustments).filter(key => {
      const [year, accountId, month] = key.split('-');
      const yearNum = parseInt(year);
      const monthNum = parseInt(month);
      
      // Only count future adjustments
      if (yearNum > currentYear) return true;
      if (yearNum === currentYear && currentMonth) {
        return monthNum > currentMonth;
      }
      return false;
    }).length;
  }, [currentScenario, currentYear, currentMonth]);

  const totalAdjustmentCount = useMemo(() => {
    if (!currentScenario?.adjustments) return 0;
    return Object.keys(currentScenario.adjustments).length;
  }, [currentScenario]);

  const historicalAdjustmentCount = totalAdjustmentCount - adjustmentCount;

  // ✅ Calculate custom items count from SCENARIO data
  const customItemCount = useMemo(() => {
    if (!currentScenario?.custom_items) return 0;
    
    return Object.values(currentScenario.custom_items).reduce(
      (sum, rows) => sum + rows.length, 
      0
    );
  }, [currentScenario]);

  // Sync edited fields when scenario changes
  useEffect(() => {
    if (currentScenario) {
      setEditedName(currentScenario.scenario_name);
      setEditedDescription(currentScenario.scenario_description || '');
    } else {
      setEditedName('');
      setEditedDescription('');
    }
  }, [currentScenario]);

  return {
    // State
    editMode,
    setEditMode,
    editedName,
    setEditedName,
    editedDescription,
    setEditedDescription,
    isSaving,
    setIsSaving,
    
    // Derived state
    currentScenario,
    isActualView,
    adjustmentCount,
    totalAdjustmentCount,
    historicalAdjustmentCount,
    customItemCount,  // ✅ Changed from tempRowCount to customItemCount
  };
};